<template>
   <Head>
      <title>{{ studyTitle }} Access</title>
   </Head>

   <PageLoader :isLoading="state.isPageLoading" :loadErrors="loadErrors" data-cy="study-access-page">
      <div class="flex h-full min-h-screen flex-col">
         <FlowselNav :study="state.study" flowName="" flowId="" />
         <section
            class="theme-brand-dark flex h-full grow items-center text-center"
            data-cy="study-access-form-container"
         >
            <div class="container flex max-w-lg flex-col items-center justify-center pt-12 md:pt-0">
               <h1 class="text-xl lg:text-3xl">{{ $t("study.study_access.heading", { study_name: studyTitle }) }}</h1>
               <p>{{ $t("study.study_access.to_participate") }}</p>
               <form class="flex w-full max-w-sm flex-col items-center p-10" @submit.prevent="onFormSubmit">
                  <h2 class="mb-6 text-xl lg:text-2xl">{{ $t("study.study_access.please_enter_code") }}</h2>
                  <CodeInput
                     id="study-invite-code"
                     class="w-60"
                     :label="$t('study.study_access.code_input_label')"
                     aria-only-label
                     :code="state.studyInvitationCode.input"
                     name="study-invite-code"
                     :placeholder="$t('study.study_access.code_input_label')"
                     is-required
                     :codeLength="6"
                     :validation-error="state.errorOnInviteCheck"
                     :show-errors="state.studyInvitationCode.showErrors"
                     @input-update="processStudyInvitationCode"
                  />
                  <LoadingButton
                     class="mt-8"
                     :color="Theme.White"
                     :is-loading="state.isFormLoading"
                     :text="$t('study.study_access.submit_button')"
                     data-cy="submit-invite-code-button"
                     @click="onFormSubmit"
                  />
               </form>

               <h2 class="sm:mt-16">{{ $t("study.study_access.not_received_invitation") }}</h2>
               <p class="my-4">
                  {{ $t("study.study_access.that_is_okay") }}
                  <RouterLink to="/home">
                     <span class="underline underline-offset-4">
                        {{ $t("study.study_access.start_link") }}
                     </span>
                  </RouterLink>
               </p>
            </div>
         </section>
      </div>
   </PageLoader>
</template>

<script setup lang="ts">
/*
    Access to the the Study access page is not possible without being redirected from a flow
    See routes-config.ts for the route configuration + middleware
*/
import { Theme } from "@/types";
import StudiesApi, { type StudyResp } from "@/data/api-layer/studiesApi";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { Head } from "@unhead/vue/components";
import { RouterLink, useRouter } from "vue-router";
import { addLocaleToRedirectPath } from "@/router/routes-config";
import FlowselNav from "@/components/flowsel/FlowselNav.vue";
import FlowParticipationsApi from "@/data/api-layer/flowParticipationsApi";
import type { AxiosError } from "axios";

const $router = useRouter();
const $route = useRoute();
const i18n = useI18n();
const { t: $t } = i18n;

interface ErrorOnInviteCheck {
   inputId: string;
   validator: "error";
   message: string;
}

interface State {
   isPageLoading: boolean;
   isFormLoading: boolean;
   studyInvitationCode: {
      input: string;
      isValid: boolean;
      showErrors: boolean;
      lastSubmittedValue: string;
   };
   errorOnInviteCheck: ErrorOnInviteCheck | null;
   study: StudyResp | null;
}

const loadErrors = ref<Error[]>([]);

const state = reactive<State>({
   isPageLoading: true,
   isFormLoading: false,
   studyInvitationCode: {
      input: "",
      isValid: false,
      showErrors: false,
      lastSubmittedValue: "",
   },
   errorOnInviteCheck: null,
   study: null as StudyResp | null,
});

// watch for change to input value to clear any previous errors
watch(
   () => state.studyInvitationCode.input,
   (newValue) => {
      state.errorOnInviteCheck = null;
   }
);

const studyKey = $route.params?.key as string;
const studyTitle = computed(() => state.study?.title || "");

enum FlowErrorCodes {
   STUDY_INVITATION_CODE_INVALID = "study_invitation_code_invalid",
   STUDY_INVITATION_CODE_CLAIMED = "study_invitation_code_claimed",
}

onMounted(async () => {
   state.isPageLoading = true;
   try {
      state.study = await StudiesApi.getOne(studyKey);
   } catch (e) {
      console.error("Error loading study access page:", e);
      state.study = null;
      loadErrors.value = [e as AxiosError];
   }
   state.isPageLoading = false;
});

function handleSubmissionError(error) {
   const errorResponseStatus = error.response?.data?.error_code;

   if (errorResponseStatus === FlowErrorCodes.STUDY_INVITATION_CODE_INVALID) {
      state.errorOnInviteCheck = {
         inputId: FlowErrorCodes.STUDY_INVITATION_CODE_INVALID,
         validator: "error",
         message: $t("validation.study_access.study_invitation_code_invalid"),
      };
      state.studyInvitationCode.showErrors = true;
   } else if (errorResponseStatus === FlowErrorCodes.STUDY_INVITATION_CODE_CLAIMED) {
      state.errorOnInviteCheck = {
         inputId: FlowErrorCodes.STUDY_INVITATION_CODE_CLAIMED,
         validator: "error",
         message: $t("validation.study_access.study_invitation_code_claimed"),
      };
      state.studyInvitationCode.showErrors = true;
   } else {
      state.errorOnInviteCheck = {
         inputId: "error_generic_msg",
         validator: "error",
         message: $t("validation.error_generic_msg"),
      };
      state.studyInvitationCode.showErrors = true;
      console.error("An error occurred during study access check submission:", error.message);
   }
}

async function onFormSubmit() {
   state.studyInvitationCode.lastSubmittedValue = state.studyInvitationCode.input;
   if (!state.studyInvitationCode.isValid) {
      state.studyInvitationCode.showErrors = true;
      return;
   }

   state.isFormLoading = true;
   state.errorOnInviteCheck = null;
   try {
      /*
         use the study invitation code try and create a flow participation before redirecting to the flow.
      */
      await FlowParticipationsApi.createFlowParticipationFromInviteCode(
         state.study.joiningFlowId,
         state.studyInvitationCode.input,
         {}
      );
      $router.push({ path: addLocaleToRedirectPath(`/flow/${state.study.joiningFlowId}`, i18n) });
   } catch (error: any) {
      handleSubmissionError(error);
      state.isFormLoading = false;
   }
}

function processStudyInvitationCode(codeInput) {
   state.studyInvitationCode.isValid = codeInput.isValid;
   state.studyInvitationCode.input = codeInput.value;
}
</script>
